import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import CustomButton from 'components/CustomButton';
import { getSubTopicsWithDetails } from 'services';
import { useServices, useSnackbar } from 'contexts';
import { capitalizeFirstLetterOfNWords } from 'utils';
import InfoTooltip, { CustomTooltip } from 'components/InfoTooltip';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
    padding: theme.spacing(7,10),
  },
  header: {
    borderBottom: '1px solid #8692A3',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '22px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  content: {
    marginTop: theme.spacing(7),
  },
  cards: {
    display: 'flex', flexDirection: 'column', gap: '20px', 
    flexWrap: 'wrap', marginTop: '8px'
  },
  cardRoot: {
    border: '2px solid',
    width: '100%',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary['clr-100'],
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardImgContainer: {
    width: '56px', height: '56px',
    borderRadius: '8px',
    border: '1px solid #CCD4DE',
    overflow: 'hidden',
  },
  skeleton: {
    borderRadius: '8px',
  },
  skeletonCardRoot: {
    width: '100%',
    border: '2px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
  },
}));

const TopicCard = ({
  name='', totalQuestions=0, completion="0", onClick=()=>{}, ...data
}) => {
  const classes = useStyles();
  
  const difficulties = [
    { label: 'Very easy', value: data?.very_easy },
    { label: 'Easy', value: data?.easy },
    { label: 'Medium', value: data?.medium },
    { label: 'Hard', value: data?.hard }
  ]
    .filter(({ value }) => value?.[1] > 0)
    .map(({ label, value }) => `${label}: ${value[0]}/${value[1] || 0}`)
    .join(', ');

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Typography variant='h4-medium'>{capitalizeFirstLetterOfNWords(name)}</Typography>
        <Box display='flex' gap={1} mt={1}>
          <Typography variant='body01-bold' color='neutral.clr-900'>
            Total Challenges: {totalQuestions} |&nbsp;
          </Typography>
          <Typography variant='body01-bold' color='neutral.clr-600'>
          {difficulties} | Completed: {Math.round(completion)}%
          </Typography>
        </Box>
      </Box>
      <CustomButton
        variant='contained' color='primary'
        endIcon={<ArrowForwardIcon/>}
        onClick={onClick}
      >
        View challenges
      </CustomButton>
    </Box>
  );
};

const TopicCardSkeleton = () => {
  const classes = useStyles();
  
  return (
    <Box className={classes.skeletonCardRoot}>
      <Box className={classes.cardContent}>
        <Skeleton variant="text" width={180} height={32} className={classes.skeleton} />
        <Box display='flex' gap={1} mt={1}>
          <Skeleton variant="text" width={350} height={24} className={classes.skeleton} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" width={160} height={40} className={classes.skeleton} />
    </Box>
  );
};

const Topics = ({ company=null, setSelected=()=>{}, handleBack=()=>{}, initialFilters=null }) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [topicsData, setTopicsData] = React.useState([]);
  const [completionRate, setCompletionRate] = React.useState(0);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const result = await service.callService(
          getSubTopicsWithDetails, {company: company?._id, ...initialFilters}
        );

        let totalRate = 0;
        Object.values(result).forEach((topic, i)=> {
          totalRate += topic.completion;
        });

        setCompletionRate(parseFloat(totalRate)/Object.keys(result).length);

        setTopicsData(result);
      } catch (error) {
        console.log(error);
        snackbar.error("Uh Oh! Unable to get the topics details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [company]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <ArrowBackIcon style={{ cursor: 'pointer', color: '#0062E1' }} onClick={handleBack}/>
          <Typography variant='h5-medium'>Topics</Typography>
        </Box>

        <CustomTooltip
          component='It represents your progress for this company. It is based on your topics completion'
        >
          <Typography
            variant='h6-medium' color='primary.clr-300'
            style={{ borderBottom: '2px dotted #135EBF'}}
          >
            Completed: {completionRate.toFixed(0)}%
          </Typography>
        </CustomTooltip>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.cards}>
        {loading ? (
          [...Array(3)].map((_, index) => (
            <TopicCardSkeleton key={`skeleton-${index}`} />
          ))
        ) : (
          Object.values(topicsData).map((data, index) => (
            <TopicCard key={`topic-${index}`} {...data} onClick={() => setSelected(data)} />
          ))
        )}
        </Box>
      </Box>
    </Box>
  )
}

export default Topics;